// Where a context or language is stored. GLOBAL ones live in the server's own
// App_Data\AI and are shared by every account - only isAdmin may see or change
// them. DOMAIN ones belong to the signed-in account.
export type AISource = 'GLOBAL' | 'DOMAIN';

export interface AIContext {
	contextID: string;          // GUID, backend-assigned on create
	source?: AISource;          // set by the server on read; ignored in a request body
	contextName: string;        // mandatory
	contextDescription: string;
	contextText: string;        // the large prompt/context body
	extensions: string[];       // empty = applies to all extensions
	platforms: string[];        // empty = applies to all platforms
	languages: string[];        // language codes, empty = applies to all languages
}

// A managed language definition. The lowercase languageCode is the identity.
export interface AILanguage {
	languageCode: string;
	languageName: string;
	source?: AISource;          // set by the server on read; ignored in a request body
}

// One entry in the AI feedback log. The server owns the fields below; the
// conversation part (tool args, agent messages, failure) has no fixed shape and
// is stored exactly as the client sent it, hence the index signature.
export interface AIFeedback {
	feedbackId: string;
	createdAt: string;          // ISO 8601, sorts correctly as text
	loggedBy: string;
	acknowledged: boolean;
	acknowledgedAt?: string | null;
	acknowledgedBy?: string | null;
	feedbackDomain?: string;    // set by the client: the domain the entry was read from
	[key: string]: unknown;
}

// Feedback ids are only unique within a domain, so the list keys on both.
export const feedbackKey = (feedback: AIFeedback): string =>
	`${feedback.feedbackDomain || ''}|${feedback.feedbackId}`;

// Appends source to an AdminAI url. Some of those endpoints already carry a
// query parameter and some do not, so the separator is picked per url.
export const withAISource = (url: string, source?: AISource): string =>
	source ? `${url}${url.includes('?') ? '&' : '?'}source=${source}` : url;

// Fixed set of Office platforms an AI context can be scoped to.
export const PLATFORM_OPTIONS: string[] = ['Word', 'Excel', 'PowerPoint', 'Outlook'];
